<script setup lang="ts">
import { computed, watch, ref } from "vue";
import { Firebase } from "@/utils/firebase-utility";
import { useUiTheme } from "@/layout/use-ui-theme.vue";
import { useRouter } from "vue-router";

import NavigationLink from "@/layout/navigation-link.vue";
import AdminDropdownItem from "@/component/AdminDropdownItem.vue";
import AdminDropdown from "@/component/AdminDropdown.vue";
import LogoSection from "@/component-bill-purser/logo-section.vue";

const router = useRouter();
const path = computed(() => router.currentRoute.value.path);

const logout = () => {
  Firebase.logout();
  location.reload();
};

const uiTheme = useUiTheme();

// アコーディオングループのrefを取得
const accordionGroupRef = ref();

function getAccordionIdByPath(path: string) {
  if (path.startsWith("/quotations")) return "quotation";
  if (path.startsWith("/invoices")) return "invoice";
  if (path.startsWith("/delivery_notes")) return "delivery-note";
  return null;
}

watch(path, (newPath) => {
  const id = getAccordionIdByPath(newPath);
  // forceOpenを呼ぶ
  accordionGroupRef.value?.forceOpen?.(id);
});
</script>

<template>
  <div class="bg-menu-base flex h-[100dvh] min-h-[100svh] w-full max-w-xs p-3 pb-0">
    <ul class="safearea-bottom flex w-full flex-col">
      <div class="-m-2">
        <AdminDropdown placement="left" button-type="none">
          <template #button>
            <div
              class="bg-base border-base-button hover:bg-base-button-hover z-20 flex h-8 w-8 cursor-pointer items-center justify-center rounded-sm border transition-all duration-150"
            >
              <i v-if="uiTheme.currentTheme.value === 'light'" class="fa-solid fa-sun text-main-text" />
              <i v-if="uiTheme.currentTheme.value === 'dark'" class="fa-solid fa-moon text-main-text" />
            </div>
          </template>
          <template v-slot:items="dropdown">
            <AdminDropdownItem
              @onClick="uiTheme.setLight"
              @onClose="dropdown.onClose"
              :class="{ 'text-main': uiTheme.themeMode.value === 'light' }"
            >
              <div class="flex w-full items-center justify-items-start gap-2">
                <div class="flex w-4 items-center justify-center">
                  <i class="fa-solid fa-sun" />
                </div>
                <span>ライトモード</span>
              </div>
            </AdminDropdownItem>
            <AdminDropdownItem
              @onClick="uiTheme.setDark"
              @onClose="dropdown.onClose"
              :class="{ 'text-main': uiTheme.themeMode.value === 'dark' }"
            >
              <div class="flex w-full items-center justify-items-start gap-2">
                <div class="flex w-4 items-center justify-center">
                  <i class="fa-solid fa-moon" />
                </div>
                <span>ダークモード</span>
              </div>
            </AdminDropdownItem>
            <AdminDropdownItem
              @onClick="uiTheme.setAuto"
              @onClose="dropdown.onClose"
              :class="{ 'text-main': uiTheme.themeMode.value === 'auto' }"
            >
              <div class="flex w-full items-center justify-items-start gap-2">
                <div class="flex w-4 items-center justify-center">
                  <i class="fa-solid fa-desktop" />
                </div>
                <span>自動設定</span>
              </div>
            </AdminDropdownItem>
          </template>
        </AdminDropdown>
      </div>

      <li class="mt-6">
        <RouterLink to="/" class="flex flex-col items-center justify-center">
          <LogoSection class="px-2" />
        </RouterLink>

      </li>

      <div class="bg-base-button my-3 h-0.5" />
      <div class="flex flex-col gap-1">
        <NavigationLink direction="vertical" icon="house" link="/" :selected="path === '/'">ホーム</NavigationLink>

        <NavigationLink icon="building" link="/organizations" :selected="path.startsWith('/organizations')">
          組織管理
        </NavigationLink>
      </div>

      <div class="mt-auto"></div>

      <div class="border-outline -mx-2 flex h-24 flex-col justify-between border-t p-3 text-xs">
        <span class="text-main-text' flex items-center gap-2">
          <i :class="'fa-solid fa-building'"></i>
          {{ Firebase.organization.value?.name ?? "管理者" }}
        </span>
        <span class="text-main-text' flex items-center gap-2">
          <i :class="'fa-solid fa-user'"></i>
          {{ Firebase.organizationUser.value?.name ?? "管理者" }}
        </span>
        <div class="flex flex-col items-end gap-2">
          <span
            class="text-main hover:text-main-hover flex cursor-pointer items-center justify-center gap-1 rounded p-1.5"
            @click="logout"
          >
            <div class="">ログアウト</div>
            <i :class="'fa-solid fa-arrow-right-from-bracket'" />
          </span>
        </div>
      </div>
    </ul>
  </div>
</template>

<style scoped lang="scss"></style>
